import { ButtonBase, Typography } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

interface Props {
  title: string;
  categoryKey: string;
  leftText: string;
  rightText: string;
  contactScore: number | undefined;
  userScore: number | undefined;
  maxWidth?: string | number;
  disableBorder?: boolean;
  /** 'default' keeps the legacy card, 'panel' renders the two-zone panel design */
  variant?: 'default' | 'panel';

  clickedChartCategoryKey?: string;

  onChartCategoryClicked?(category: string | string[]): void;
}

const isAlmostEqual = (firstVal: number, secondVal: number) => {
  const difference = Math.abs(firstVal - secondVal);
  return difference > 1.25 ? false : true;
};

const MatchCompareGraph: React.FC<Props> = ({
  title,
  categoryKey,
  leftText,
  rightText,
  contactScore,
  userScore,
  maxWidth,
  disableBorder,
  variant = 'default',
  clickedChartCategoryKey,
  onChartCategoryClicked,
}) => {
  const { classes, cx } = useStyles({
    contactScoreNorm: (contactScore ?? 0) * 100,
    userScoreNorm: (userScore ?? 0) * 100,
    disableBorder,
    maxWidth,
  });

  const isPanel = variant === 'panel';

  return (
    <div className={cx(classes.languageMatch, isPanel ? classes.panelWrapper : null)}>
      <div className={classes.content}>
        <ButtonBase
          onClick={() => (!_.isNil(onChartCategoryClicked) ? onChartCategoryClicked(categoryKey) : undefined)}
          disabled={_.isNil(onChartCategoryClicked)}
          style={{ width: '100%', justifyContent: isPanel ? 'flex-start' : 'center' }}
        >
          <Typography
            className={cx(
              classes.title,
              isPanel ? classes.panelTitle : null,
              !_.isNil(onChartCategoryClicked) ? (clickedChartCategoryKey !== categoryKey ? classes.clickable : classes.clicked) : null
            )}
          >
            {title}
          </Typography>
        </ButtonBase>
        <div className={classes.graph}>
          {!_.isNil(contactScore) ? <div className={classes.contactScore} /> : null}
          {!_.isNil(userScore) ? <div className={classes.userScore} /> : null}
          <div className={classes.leftText}>
            <Typography className={classes.graphText}>{leftText}</Typography>
          </div>
          <div className={classes.rightText}>
            <Typography className={classes.graphText}>{rightText}</Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles<{ contactScoreNorm: number; userScoreNorm: number; disableBorder?: boolean; maxWidth?: string | number }>()(
  (theme, { contactScoreNorm, userScoreNorm, disableBorder, maxWidth }) => ({
    languageMatch: {
      width: '100%',
      background: '#FFF',
      border: disableBorder ? 'unset' : `2px solid ${theme.palette.backgrounds.main}`,
      padding: '8px 16px',
      display: 'flex',
      justifyContent: 'center',
    },
    content: {
      width: '100%',
      maxWidth: maxWidth ? maxWidth : '300px',
    },
    title: {
      fontSize: theme.typography.pxToRem(13),
      fontWeight: 'bold',
      textTransform: 'uppercase',
      textAlign: 'center',
      whiteSpace: 'pre-wrap',
      marginBottom: '10px',
    },
    panelWrapper: {
      padding: 0,
      justifyContent: 'flex-start',
    },
    panelTitle: {
      fontSize: theme.typography.pxToRem(14),
      fontWeight: 500,
      textTransform: 'none',
      textAlign: 'start',
      width: '100%',
      marginBottom: '8px',
    },
    clickable: {
      textDecoration: 'underline',
      '&:hover': {
        color: theme.palette.primary.main,
        textDecoration: 'underline',
      },
    },
    clicked: {
      color: theme.palette.primary.main,
      textDecoration: 'underline',
    },
    graph: {
      position: 'relative',
      height: '4px',
      width: '100%',
      background: theme.palette.greys.light,
      display: 'flex',
      alignItems: 'center',
      margin: '5.5px 0 24px 0',
    },
    contactScore: {
      height: isAlmostEqual(contactScoreNorm, userScoreNorm) ? '10px' : '15px',
      width: isAlmostEqual(contactScoreNorm, userScoreNorm) ? '10px' : '15px',
      zIndex: isAlmostEqual(contactScoreNorm, userScoreNorm) ? '9999' : undefined,
      position: 'absolute',
      background: theme.palette.analysisSecondary.main,
      left: `calc(${contactScoreNorm}% - 7.5px)`,
      border: '1px solid #FFF',
    },
    userScore: {
      height: '15px',
      width: '15px',
      position: 'absolute',
      background: theme.palette.primary.main,
      left: `calc(${userScoreNorm}% - 7.5px)`,
      border: '1px solid #FFF',
    },
    graphText: {
      fontSize: theme.typography.pxToRem(11),
      textTransform: 'capitalize',
    },
    leftText: {
      position: 'absolute',
      textAlign: 'left',
      left: 0,
      top: '12px',
    },
    rightText: {
      position: 'absolute',
      textAlign: 'right',
      right: 0,
      top: '12px',
    },
    motiveItemText: {
      fontSize: theme.typography.pxToRem(12),
    },
  })
);

export default MatchCompareGraph;
